import { Injectable, inject } from "@angular/core";
import {BehaviorSubject, Observable, tap} from "rxjs";
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {environment} from "../../environment/environment";
import {Player} from "../model/players";
import {LineUp} from "../model/lineUp";
import {Position} from "../model/position";
import {fakeLineUpDemo} from './lineUpDemo';

@Injectable({
  providedIn: 'root'
})

export class LineUpService {
  private http = inject(HttpClient);

  public home: LineUp = this.createEmptyLineUpObject();

  public away: LineUp = this.createEmptyLineUpObject();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  lineupUpdates$ = new BehaviorSubject<void>(null);

  loadLineUp(gameId: string): Observable<any> {
    //S'assurer que les 2 équipes sont vides avant de les remplir avec la call.
    this.home = this.createEmptyLineUpObject();
    this.away = this.createEmptyLineUpObject();
    return this.http.get<any>(`${environment.apiUrl}/game/${gameId}/lineup`).pipe(
      map(response => {
        return this.fillLineUp(response);
      })
    );
  }

  loadLineUpDemo(response = fakeLineUpDemo) {
    this.home = this.createEmptyLineUpObject();
    this.away = this.createEmptyLineUpObject();
    return this.fillLineUp(response);
  }

  private fillLineUp(response: any) {
    this.home.teamId = response.homeLineups.teamId
    this.home.seasonId = response.homeLineups.seasonId
    this.home.fullTeam = response.homeLineups.players.sort((a: Player, b: Player) => a.jerseyNumber - b.jerseyNumber);

    this.away.teamId = response.awayLineups.teamId
    this.away.seasonId = response.awayLineups.seasonId
    this.away.fullTeam = response.awayLineups.players.sort((a: Player, b: Player) => a.jerseyNumber - b.jerseyNumber);

    this.fillActivePlayerPositions(this.home, response.homeLineups.players);
    this.fillActivePlayerPositions(this.away, response.awayLineups.players);

    return {
      home: this.home,
      away: this.away
    };
  }

  createEmptyLineUpObject() {
    return {
      teamId: '',
      seasonId: '',
      kickers: [],
      passers: [],
      receivers: [],
      returners: [],
      tacklers: [],
      rushers: [],
      offensive: [],
      defensive: [],
      activeStatuses: [],
      fullTeam: []
    };
  }

  public fillActivePlayerPositions(team:LineUp, lineups: Player[]) {
    const kickersSet = new Set<number>();
    const passersSet = new Set<number>();
    const receiversSet = new Set<number>();
    const rushersSet = new Set<number>();
    const returnersSet = new Set<number>();
    const tacklersSet = new Set<number>();
    const offensiveSet = new Set<number>();
    const defensiveSet = new Set<number>();

    lineups.forEach(player => {
      player.playerPositions.forEach(pos => {
        const jerseyNumber = player.gamesPlayed[0]?.gameJerseyNumber || player.jerseyNumber;

        if (player.gamesPlayed[0]?.gameJerseyNumber !== null) {
          switch (pos.position) {
            case Position.K:
              kickersSet.add(jerseyNumber);
              defensiveSet.add(jerseyNumber);
              break;
            case Position.QB:
              passersSet.add(jerseyNumber);
              offensiveSet.add(jerseyNumber);
              break;
            case Position.WR:
            case Position.RB:
              receiversSet.add(jerseyNumber);
              rushersSet.add(jerseyNumber);
              returnersSet.add(jerseyNumber);
              offensiveSet.add(jerseyNumber);
              break;
            case Position.DB:
            case Position.LB:
            case Position.DL:
              tacklersSet.add(jerseyNumber);
              defensiveSet.add(jerseyNumber);
              break;
            case Position.OL:
              offensiveSet.add(jerseyNumber);
              break;
          }
        }
      });
    });

    team.kickers = Array.from(kickersSet).sort((a, b) => a - b);
    team.passers = Array.from(passersSet).sort((a, b) => a - b);
    team.receivers = Array.from(receiversSet).sort((a, b) => a - b);
    team.rushers = Array.from(rushersSet).sort((a, b) => a - b);
    team.returners = Array.from(returnersSet).sort((a, b) => a - b);
    team.tacklers = Array.from(tacklersSet).sort((a, b) => a - b);
    team.offensive = Array.from(offensiveSet).sort((a, b) => a - b);
    team.defensive = Array.from(defensiveSet).sort((a, b) => a - b);

    team.activeStatuses = lineups.map(player => player.gamesPlayed.length > 0);
  }

  private deactivatePlayerPositions(player:Player, lineUp: LineUp) {
     player.playerPositions.forEach(pos => {
       switch (pos.position) {
          case Position.K:
            lineUp.kickers = lineUp.kickers.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            lineUp.defensive = lineUp.defensive.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            break;
          case Position.QB:
            lineUp.passers = lineUp.passers.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            lineUp.offensive = lineUp.offensive.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            break;
          case Position.WR:
          case Position.RB:
            lineUp.receivers = lineUp.receivers.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            lineUp.rushers = lineUp.rushers.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            lineUp.returners = lineUp.returners.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            lineUp.offensive = lineUp.offensive.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            break;
          case Position.DB:
          case Position.LB:
          case Position.DL:
            lineUp.tacklers = lineUp.tacklers.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            lineUp.defensive = lineUp.defensive.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            break;
          case Position.OL:
            lineUp.offensive = lineUp.offensive.filter(jerseyNumber => jerseyNumber !== player.jerseyNumber);
            break;
       }
     });
  }

  activatePlayerToLineUp(player: Player, lineup: LineUp) {
    //Player référence l'élément player (la row avec le checkbox, donc a cette row, j'ajoute le gameplayed pour qu'il devient cocher sans call loadLineup)
    player.gamesPlayed.push({gameJerseyNumber: player.jerseyNumber});
    this.fillActivePlayerPositions(lineup, lineup.fullTeam);
  }

  deactivatePlayerFromLineUp(player: Player, lineup: LineUp) {
    player.gamesPlayed = [];
    this.deactivatePlayerPositions(player, lineup);
  }

  setPlayerInGame(gameId: string, player: Player, isInGame: boolean): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/game/${gameId}/lineup/set-player-game-status`, {isInGame, player}).pipe(
      tap(() => {
        this.lineupUpdates$.next();
      })
    );
  }

  postPlayerToLineUp(gameId: string, player: Player): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/game/${gameId}/lineup/add-player`, {player}).pipe(
        tap(() => {
            this.lineupUpdates$.next();
        })
    );
  }

    putEditPlayerToLineUp(gameId: string, player: Player): Observable<any> {
        return this.http.put<any>(`${environment.apiUrl}/game/${gameId}/lineup/edit-player`, {player}).pipe(
            tap(() => {
                this.lineupUpdates$.next();
            })
        );
    }
}
